import { useEffect, useRef, useState } from 'react';
import { Mic, Upload, Loader2, AlertCircle, Play, Check } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';

const PROVIDERS = [{ id: 'elevenlabs', label: 'ElevenLabs' }, { id: 'kling', label: 'Kling native' }, { id: 'upload', label: 'My recording' }];

export const PresenterVoicePanel = ({ video, onUpdate, onRefresh }) => {
  const voice = video.voice || {};
  const [elVoices, setElVoices] = useState([]);
  const [klingVoices, setKlingVoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/elevenlabs/voices').then(r => setElVoices(r.data.voices || [])).catch(() => {});
    Promise.all([api.get('/kling/preset-voices').catch(() => ({ data: [] })), api.get('/kling/custom-voices').catch(() => ({ data: [] }))])
      .then(([p, c]) => setKlingVoices([...(c.data || []).map(v => ({ ...v, type: 'custom' })), ...(p.data || [])]));
  }, []);

  const setVoice = (patch) => onUpdate({ voice: patch }, { silent: true });
  const pickVoice = (provider, id) => {
    const list = provider === 'elevenlabs' ? elVoices : klingVoices;
    const v = list.find(x => x.voice_id === id);
    setVoice({ provider, voice_id: id || null, voice_name: v ? (v.name || v.voice_name) : '' });
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { await api.post(`/presenter/${video.id}/audio/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); await onRefresh(); toast.success('Audio ready'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    try { await api.post(`/presenter/${video.id}/audio/generate`); await onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail?.split('|').pop() || 'Could not start'); }
  };

  const generating = video.audio_status === 'generating';
  const list = voice.provider === 'elevenlabs' ? elVoices : klingVoices;

  return (
    <section className="ff-card rounded-2xl p-5" data-testid="presenter-voice-panel">
      <h2 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#E879F9] flex items-center gap-2 mb-3"><Mic className="w-3.5 h-3.5" /> 3 · Voice</h2>

      <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06] w-fit mb-3">
        {PROVIDERS.map(p => (
          <button key={p.id} data-testid={`voice-provider-${p.id}`} onClick={() => setVoice({ provider: p.id, voice_id: null, voice_name: '' })}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${voice.provider === p.id ? 'bg-[#E879F9]/15 text-white' : 'text-[#8A8A93] hover:text-white'}`}>{p.label}</button>
        ))}
      </div>

      {voice.provider === 'upload' ? (
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="voice-upload-input" />
          <button data-testid="voice-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="ff-btn-secondary text-xs flex items-center gap-2">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {video.audio_path ? 'Replace recording' : 'Choose MP3 / WAV / M4A'}
          </button>
          <span className="text-[11px] text-[#8A8A93]">2 s – 5 min. We convert it for Kling automatically.</span>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select data-testid="voice-select" value={voice.voice_id || ''} onChange={e => pickVoice(voice.provider, e.target.value)} className="ff-input flex-1 rounded-lg px-3 py-2 text-sm min-w-0">
            <option value="">{list.length ? 'Pick a voice…' : `No ${voice.provider === 'elevenlabs' ? 'ElevenLabs' : 'Kling'} voices (check the API key in Team Settings)`}</option>
            {list.map(v => <option key={v.voice_id} value={v.voice_id}>{(v.name || v.voice_name)}{v.type === 'custom' || v.category === 'cloned' ? ' · cloned' : ''}</option>)}
          </select>
          {voice.provider === 'kling' && (
            <select data-testid="voice-language" value={voice.language || 'en'} onChange={e => setVoice({ language: e.target.value })} className="ff-input rounded-lg px-2 py-2 text-xs">
              <option value="en">English</option><option value="zh">Chinese</option>
            </select>
          )}
          <button data-testid="voice-generate-btn" onClick={generate} disabled={generating || !voice.voice_id || !(video.script || '').trim()} className="ff-btn-primary text-xs flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} {video.audio_path ? 'Regenerate voice' : 'Generate voice'}
          </button>
        </div>
      )}

      {generating && <p className="text-[11px] text-[#E879F9] mt-2 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {video.progress || 'Working…'}</p>}
      {video.audio_status === 'failed' && <p data-testid="voice-error" className="text-[11px] text-red-400 mt-2 flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {video.audio_error}</p>}
      {video.audio_path && video.audio_status === 'ready' && (
        <div className="mt-3 flex items-center gap-3" data-testid="voice-ready">
          <span className="text-[11px] text-[#10B981] flex items-center gap-1 whitespace-nowrap"><Check className="w-3 h-3" /> {Math.round(video.audio_duration || 0)}s</span>
          <audio controls src={getImageUrl(video.audio_path)} className="h-8 flex-1 min-w-0" data-testid="voice-audio-player" />
        </div>
      )}
      {video.audio_path && video.audio_status === null && !generating && (
        <p className="text-[11px] text-amber-400 mt-2">Script or voice changed — regenerate the voice track before rendering.</p>
      )}
    </section>
  );
};
